import { Button, EditableText, Icon } from "@blueprintjs/core"
import { isEmpty } from "lodash"
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react"
import { Controller, FormProvider, useForm } from "react-hook-form"

import { useQueryClient } from "@tanstack/react-query"
import { FlexContainer } from "../../../../styled/flexbox.styled"
import { ConfirmDialog } from "../../../components"
import { createToaster } from "../../../utils/createToaster"
import { ErrorIconWithTooltip } from "../../../components"
import Sectors from "../components/Sectors"
import useUpdateTradingCompanyMutation from "../hooks/useUpdateTradingCompanyMutation"
import { TradingCompanyDetail } from "../types"
import { formatCompany } from "../utils/formatCompany"
import CompanyFields from "./CompanyFields"
import { ActionButtonGroup, FieldsContainer, CompanyName } from "./CompanyForm.styled"
import CompanyMetadata from "./CompanyMetadata"

interface ICompanyFormProps {
  selectedCompany: TradingCompanyDetail
  setSelectedCompanyId: Dispatch<SetStateAction<number | undefined>>
}

type confirmDialiogInitialStateProps = {
  open: boolean
  title: string
  dialogText: string
  handleClick: (() => void) | null
  confirmButtonText: string
}

const confirmDialiogInitialState: confirmDialiogInitialStateProps = {
  open: false,
  title: "",
  dialogText: "",
  handleClick: null,
  confirmButtonText: "",
}

const CompanyForm = ({ selectedCompany, setSelectedCompanyId }: ICompanyFormProps) => {
  const queryClient = useQueryClient()
  const [confirmDialog, setConfirmDialog] = useState(confirmDialiogInitialState)
  // state variable that changes whenever the form is reset. This will trigger a rerender of the EditableText component.
  const [resetKey, setResetKey] = useState(0)

  const methods = useForm<TradingCompanyDetail>({
    defaultValues: selectedCompany,
  })

  // Use effect to reset form when selectedCompany changes
  useEffect(() => {
    if (selectedCompany) {
      methods.reset(selectedCompany)
    }
  }, [selectedCompany, methods])

  //Mutate function
  const updateCompanyMutation = useUpdateTradingCompanyMutation({
    onSuccess: () => {
      // Invalidate both queries to refresh the updated company
      queryClient.invalidateQueries({ queryKey: ["trading_companies"] })
      queryClient.invalidateQueries({ queryKey: ["trading_company", selectedCompany.id] })
      createToaster({
        message: `${selectedCompany.name} Company updated with success`,
        intent: "success",
      })
    },
    onError: () => {
      methods.reset()
      createToaster({
        message: `An error occurred updating the Company ${selectedCompany.name}, please refresh the page`,
        intent: "danger",
      })
    },
  })

  /**
   * Callback to submit Form
   */
  const submitForm = useCallback(
    (company: TradingCompanyDetail) => {
      const formattedCompany = formatCompany(company)
      updateCompanyMutation.mutate({ companyId: company.id, company: formattedCompany })
      setConfirmDialog(confirmDialiogInitialState)
    },
    [updateCompanyMutation],
  )

  const handleSubmitForm = () => {
    setConfirmDialog({
      open: true,
      title: "Update company",
      dialogText: "You are going update this company",
      handleClick: () => methods.handleSubmit(submitForm)(),
      confirmButtonText: "Update",
    })
  }

  /**
   * Callback to reset Form
   */
  const resetForm = useCallback(() => {
    methods.reset()
    setConfirmDialog(confirmDialiogInitialState)
    setResetKey(prev => prev + 1) // Update resetKey to force rerender
  }, [methods])

  const handleResetForm = () => {
    setConfirmDialog({
      open: true,
      title: "Reset company",
      dialogText: "You are going to cancel your change.",
      handleClick: resetForm,
      confirmButtonText: "Reset",
    })
  }

  return (
    <FormProvider {...methods}>
      <form>
        <FlexContainer alignItems="center" justifyContent="space-between" gap="15px">
          <FlexContainer alignItems="center" justifyContent="flex-end">
            <Icon icon="label" size={28} />
            <Controller
              control={methods.control}
              name="name"
              rules={{
                required: "Name is required",
              }}
              render={({ field: { onChange, value }, fieldState: { isDirty } }) => (
                <>
                  <CompanyName isDirty={isDirty}>
                    <EditableText
                      key={resetKey} // Use resetKey as key
                      value={value}
                      onChange={onChange}
                      placeholder="Edit Name"
                      multiline={true}
                    />
                  </CompanyName>
                  {methods.formState.errors.name && (
                    <ErrorIconWithTooltip errorMessage={methods.formState.errors.name.message} />
                  )}
                </>
              )}
            />
          </FlexContainer>
          <CompanyMetadata selectedCompany={selectedCompany} />
        </FlexContainer>

        <FieldsContainer flexDirection="column" gap="20px">
          <CompanyFields control={methods.control} />
          <Sectors />
          {methods.formState.isDirty && (
            <FlexContainer justifyContent="flex-end">
              <ActionButtonGroup large fill>
                <Button text="Reset" icon="reset" onClick={handleResetForm} />
                <Button
                  text="Update"
                  icon="upload"
                  intent="primary"
                  onClick={handleSubmitForm}
                  disabled={!isEmpty(methods.formState.errors)}
                />
              </ActionButtonGroup>
            </FlexContainer>
          )}
        </FieldsContainer>

        <ConfirmDialog
          title={confirmDialog.title}
          dialogText={confirmDialog.dialogText}
          open={confirmDialog.open}
          close={() => setConfirmDialog(confirmDialiogInitialState)}
          handleClick={confirmDialog.handleClick as () => void}
          confirmButtonText={confirmDialog.confirmButtonText}
        />
      </form>
    </FormProvider>
  )
}

export default CompanyForm
